import { useMemo, memo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { renderBlock, StreamingLoadingIndicator, UserMessageBlock } from '../MessageBlockRenderer';
import ChatStreamGroup from './ChatStreamGroup';
import BrandIcon from '../BrandIcon';

function BrandHeader({ brand }) {
    return (
        <div className="flex items-center gap-2 mb-1 mt-5">
            <BrandIcon icon={brand.icon} size={22} color={brand.color} />
            <span className="text-[15px] font-semibold text-gray-700">{brand.name}</span>
        </div>
    );
}

const ChatStream = memo(function ChatStream({
    blocks,
    isStreaming = false,
    brand = null,
    placeholderHeight = 0,
    liveRef = null,
    onApprove,
    onReject,
    onEdit,
    onRetry,
    isSubmitting,
    showApprovalUI,
    sessionId = null,
    cta = null,
    onCtaClick = null,
}) {
    const renderOptions = { onApprove, onReject, onEdit, onRetry, isSubmitting, showApprovalUI, sessionId };

    const groupedItems = useMemo(() => {
        const items = [];
        let currentGroup = null;

        for (const block of blocks) {
            if (block.type === 'file_processing' && !block.isStreaming) continue;
            if (block.type === 'group_start') {
                if (currentGroup) items.push(currentGroup);
                currentGroup = { type: '_group', blocks: [], summary: block.summary || '', isOpen: true, message_type: block.message_type };
                continue;
            }
            if (block.type === 'group_end') {
                if (currentGroup) {
                    currentGroup.summary = block.summary || currentGroup.summary;
                    currentGroup.isOpen = false;
                    items.push(currentGroup);
                    currentGroup = null;
                }
                continue;
            }
            if (block.type === 'user') {
                if (currentGroup) { items.push(currentGroup); currentGroup = null; }
                items.push(block);
                continue;
            }
            if (block.type === 'text' && !block.isPart) {
                if (currentGroup) { items.push(currentGroup); currentGroup = null; }
                items.push(block);
                continue;
            }
            if (block.type === 'terminal_error' || block.type === 'terminal_user_stopped') {
                if (currentGroup) { currentGroup.isOpen = false; items.push(currentGroup); currentGroup = null; }
                items.push(block);
                continue;
            }
            if (block.type === 'approval_request') {
                if (currentGroup) {
                    currentGroup.blocks.push(block);
                } else {
                    items.push(block);
                }
                continue;
            }
            if (currentGroup) {
                currentGroup.blocks.push(block);
            } else {
                items.push(block);
            }
        }

        if (currentGroup) {
            if (!isStreaming) currentGroup.isOpen = false;
            items.push(currentGroup);
        }
        return items;
    }, [blocks, isStreaming]);

    const hasPendingApproval = blocks.some(b => b.type === 'approval_request' && !b.isResolved);
    const showLoading = isStreaming && !hasPendingApproval;

    let needBrand = false;

    const lastUserIndex = useMemo(() => {
        for (let i = groupedItems.length - 1; i >= 0; i--) {
            if (groupedItems[i].type === 'user') return i;
        }
        return -1;
    }, [groupedItems]);

    const hasPlaceholder = placeholderHeight > 0 && lastUserIndex >= 0;

    const renderItem = (item, index) => {
        if (item.type === 'user') {
            needBrand = !!brand;
            return <UserMessageBlock key={`user-${index}`} content={item.content} attachments={item.attachments} />;
        }

        const showBrand = needBrand || (brand && item.message_type === 'chat');
        if (needBrand) needBrand = false;

        if (item.type === '_group') {
            return (
                <div key={`group-${index}`}>
                    {showBrand && <BrandHeader brand={brand} />}
                    <ChatStreamGroup blocks={item.blocks} isOpen={item.isOpen} summary={item.summary} renderOptions={renderOptions} />
                </div>
            );
        }

        if (!showBrand) return renderBlock(item, index, renderOptions);

        return (
            <div key={`block-${index}`}>
                <BrandHeader brand={brand} />
                {renderBlock(item, index, renderOptions)}
            </div>
        );
    };

    return (
        <div className="space-y-0">
            {hasPlaceholder ? (
                <>
                    {groupedItems.slice(0, lastUserIndex).map((item, i) => renderItem(item, i))}
                    <div ref={liveRef} style={{ minHeight: placeholderHeight }}>
                        {groupedItems.slice(lastUserIndex).map((item, i) => renderItem(item, lastUserIndex + i))}
                        {needBrand && <BrandHeader brand={brand} />}
                        {showLoading && <StreamingLoadingIndicator />}
                    </div>
                </>
            ) : (
                <>
                    {groupedItems.map((item, i) => renderItem(item, i))}
                    {needBrand && <BrandHeader brand={brand} />}
                    {showLoading && <StreamingLoadingIndicator />}
                </>
            )}
            {cta && !isStreaming && onCtaClick && (
                <div className="pt-4 space-y-3">
                    {cta.intro && (
                        <div className="text-sm font-medium text-gray-600">
                            <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{cta.intro}</ReactMarkdown>
                        </div>
                    )}
                    <div className="space-y-2">
                        {(cta.items || []).map((item, i) => (
                            <button
                                key={i}
                                onClick={() => onCtaClick(item)}
                                className="w-full text-left px-4 py-3 rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors"
                            >
                                <div className="font-medium text-sm text-gray-900">{item.label}</div>
                                {item.description && (
                                    <div className="text-xs text-gray-500 mt-0.5">{item.description}</div>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
});

export default ChatStream;
